import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Pencil, Upload, RotateCcw, Trash2, X, Loader2, Link as LinkIcon, FolderOpen, Search, ExternalLink, Database } from "lucide-react";
import { useEditMode } from "@/contexts/CmsContext";
import { useLanguage } from "@/contexts/LanguageContext";
import SmartImage from "@/components/cms/SmartImage";
import MediaPicker from "@/components/admin/MediaPicker";
import BunnyStoragePicker from "@/components/admin/BunnyStoragePicker";
import {
  ensureImageLoaded, subscribeImage, getCachedImage, resolveUrl,
  uploadSlotImage, clearSlotImage, saveSlotImageMeta,
} from "@/lib/slotCache";
import { reportSlotDefault } from "@/lib/slotDefaultsCapture";

// <EditableImage slot="home.hero.bg" fallback={IMG} alt="" aspectRatio="16/9" />
//
//   OFF mode → renders a SmartImage with the slot URL (or fallback).
//   ON mode  → adds a pencil button in the corner that opens an edit modal
//              (upload + clear + alt). Hover overlay shows the slot id.
export const EditableImage = ({
  slot,
  fallback,
  alt = "",
  aspectRatio,
  priority = false,
  className = "",
  imgClassName = "",
  wrapperClassName = "",
  ...rest
}) => {
  const { imageEditMode } = useEditMode();
  const { lang } = useLanguage();
  const [data, setData] = useState(() => getCachedImage(slot));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    ensureImageLoaded();
    const unsubscribe = subscribeImage(slot, (next) => {
      setData(next ? { ...next } : next);
    });
    return unsubscribe;
  }, [slot]);

  const cleared = !!data?.cleared;
  const definitive = cleared ? null : resolveUrl(data?.url);
  const src = definitive || fallback;
  const effectiveAlt = (data?.alt_i18n && data.alt_i18n[lang]) || alt;

  // Capture the brand-default URL for /admin/images thumbnails. Only
  // reports when the DB has no active override — otherwise the
  // reported URL would be an admin's customisation, not the default.
  // See slotDefaultsCapture.js for dedupe + batching semantics.
  useEffect(() => {
    const dbHasOverride = !!data?.url && !cleared;
    if (!dbHasOverride && fallback) {
      reportSlotDefault(slot, fallback);
    }
  }, [slot, fallback, data?.url, cleared]);

  return (
    <span
      className={`editable-image-wrapper relative block ${wrapperClassName} ${imageEditMode ? "is-editing" : ""}`}
      data-cms-image-slot={slot}
    >
      <SmartImage
        src={src}
        fallback={fallback}
        alt={effectiveAlt}
        aspectRatio={aspectRatio}
        priority={priority}
        className={className}
        imgClassName={imgClassName}
        {...rest}
      />
      {imageEditMode && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(true); }}
          className="editable-image-edit-btn"
          data-edit-allow="true"
          data-testid={`editable-edit-btn-${slot}`}
          aria-label={`Edit image ${slot}`}
        >
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.8} />
          <span>Edit</span>
        </button>
      )}
      {editing && createPortal(
        <ImageEditModal
          slot={slot}
          currentUrl={src}
          currentAlt={effectiveAlt}
          onClose={() => setEditing(false)}
        />,
        document.body
      )}
    </span>
  );
};

// ── Modal ──────────────────────────────────────────────────────────────
export const ImageEditModal = ({ slot, currentUrl, currentAlt, currentAltI18n, onClose }) => {
  const { lang } = useLanguage();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(null);  // 'upload' | 'url' | 'clear' | 'alt' | 'library' | null
  const [pasteUrl, setPasteUrl] = useState("");
  const [altDraft, setAltDraft] = useState({ en: "", fr: "", es: "" });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bunnyOpen, setBunnyOpen] = useState(false);

  // ── Pexels search state ───────────────────────────────────────────────
  const [pexelsQuery, setPexelsQuery] = useState("");
  const [pexelsStatus, setPexelsStatus] = useState("idle"); // idle | loading | results | empty | error
  const [pexelsPhotos, setPexelsPhotos] = useState([]);
  const [pexelsError, setPexelsError] = useState("");
  const [pexelsApplyingId, setPexelsApplyingId] = useState(null);
  const [pexelsPage, setPexelsPage] = useState(1);
  const [pexelsHasMore, setPexelsHasMore] = useState(false);
  const [pexelsLoadingMore, setPexelsLoadingMore] = useState(false);
  const pexelsActiveQuery = useRef("");
  const pexelsSentinelRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL || "";

  const PEXELS_PER_PAGE = 18;
  const PEXELS_MAX_PAGE = 100;

  // Unified fetch: append=false → new search (resets state); append=true → next-page append.
  const fetchPexelsPage = async (rawQuery, page, append) => {
    const q = (rawQuery ?? "").trim();
    if (!q) return;
    if (append) {
      setPexelsLoadingMore(true);
    } else {
      setPexelsStatus("loading");
      setPexelsError("");
      setPexelsPhotos([]);
      setPexelsHasMore(false);
      pexelsActiveQuery.current = q;
    }
    try {
      const res = await fetch(
        `${API}/api/pexels/search?query=${encodeURIComponent(q)}&per_page=${PEXELS_PER_PAGE}&page=${page}`,
        { cache: "no-store" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const photos = Array.isArray(data.photos) ? data.photos : [];
      // Guard against a race where the user launched a newer search while
      // this request was in flight — drop stale responses.
      if (pexelsActiveQuery.current !== q) return;
      setPexelsPhotos((prev) => (append ? [...prev, ...photos] : photos));
      setPexelsPage(page);
      setPexelsHasMore(Boolean(data.next_page) && page < PEXELS_MAX_PAGE);
      if (!append) {
        setPexelsStatus(photos.length ? "results" : "empty");
      }
    } catch (err) {
      if (append) {
        // Keep the existing results visible; just stop trying to load more.
        setPexelsHasMore(false);
      } else {
        setPexelsError(err.message || "Search failed");
        setPexelsStatus("error");
      }
    } finally {
      if (append) setPexelsLoadingMore(false);
    }
  };

  const runPexelsSearch = (rawQuery) => {
    const q = (rawQuery ?? pexelsQuery).trim();
    if (!q) return;
    fetchPexelsPage(q, 1, false);
  };

  // Infinite scroll: fire the next page when the sentinel becomes visible
  // within the grid's actual scroll container (may be the grid itself in
  // compact modal, or an ancestor in fullscreen modal).
  useEffect(() => {
    if (pexelsStatus !== "results") return undefined;
    if (!pexelsHasMore) return undefined;
    const node = pexelsSentinelRef.current;
    if (!node) return undefined;

    // Walk up the DOM to find the nearest ancestor that actually scrolls,
    // so the observer resolves visibility against that surface rather
    // than the browser viewport.
    let scrollRoot = null;
    let cursor = node.parentElement;
    while (cursor && cursor !== document.body) {
      const style = window.getComputedStyle(cursor);
      const oy = style.overflowY;
      if ((oy === "auto" || oy === "scroll") && cursor.scrollHeight > cursor.clientHeight) {
        scrollRoot = cursor;
        break;
      }
      cursor = cursor.parentElement;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (pexelsLoadingMore) return;
        const nextPage = pexelsPage + 1;
        if (nextPage > PEXELS_MAX_PAGE) return;
        fetchPexelsPage(pexelsActiveQuery.current, nextPage, true);
      },
      { root: scrollRoot, rootMargin: "200px", threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [pexelsStatus, pexelsHasMore, pexelsPage, pexelsLoadingMore]);

  const handlePickPexels = async (photo) => {
    const url = photo?.src?.large2x || photo?.src?.large || photo?.src?.original;
    if (!url) return;
    setPexelsApplyingId(photo.id);
    setBusy("pexels");
    try {
      await saveSlotImageMeta(slot, {
        url,
        source: "pexels",
        credit: {
          photographer: photo.photographer || "",
          photographer_url: photo.photographer_url || "",
          source_url: photo.url || "",
          provider: "pexels",
          provider_id: String(photo.id || ""),
        },
      });
      onClose();
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setBusy(null);
      setPexelsApplyingId(null);
    }
  };

  // ESC closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("upload");
    try { await uploadSlotImage(slot, file); onClose(); }
    catch (err) { alert(`Upload failed: ${err.message}`); }
    finally { setBusy(null); }
  };

  const handlePasteUrl = async () => {
    if (!pasteUrl.trim()) return;
    setBusy("url");
    try { await saveSlotImageMeta(slot, { url: pasteUrl.trim(), source: "external" }); onClose(); }
    catch (err) { alert(`Save failed: ${err.message}`); }
    finally { setBusy(null); }
  };

  const handleClear = async () => {
    if (!window.confirm("Reset this image to the original default?")) return;
    setBusy("clear");
    try { await clearSlotImage(slot); onClose(); }
    catch (err) { alert(`Clear failed: ${err.message}`); }
    finally { setBusy(null); }
  };

  const handlePickFromLibrary = async (asset) => {
    if (!asset) return;
    setPickerOpen(false);
    setBusy("library");
    try {
      await saveSlotImageMeta(slot, { url: asset.url, source: "media_library" });
      onClose();
    } catch (err) { alert(`Save failed: ${err.message}`); }
    finally { setBusy(null); }
  };

  const handlePickFromBunny = async (file) => {
    if (!file || !file.public_url) return;
    setBunnyOpen(false);
    setBusy("bunny");
    try {
      await saveSlotImageMeta(slot, {
        url: file.public_url,
        source: "bunny_storage",
        credit: {
          provider: "bunny_storage",
          provider_id: file.path || file.name || "",
        },
      });
      onClose();
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const handleSaveAlt = async () => {
    setBusy("alt");
    try {
      const clean = Object.fromEntries(
        Object.entries(altDraft).filter(([, v]) => (v || "").trim().length)
      );
      await saveSlotImageMeta(slot, { alt_i18n: clean });
      onClose();
    } catch (err) { alert(`Save failed: ${err.message}`); }
    finally { setBusy(null); }
  };

  // Pre-fill alt from current. When the caller passes a full i18n object
  // (e.g. from /admin/images which has access to alt_i18n in the listing),
  // hydrate all three languages so re-saving doesn't blank out the locales
  // the editor wasn't actively viewing.
  useEffect(() => {
    if (currentAltI18n && typeof currentAltI18n === "object") {
      setAltDraft({
        en: currentAltI18n.en || "",
        fr: currentAltI18n.fr || "",
        es: currentAltI18n.es || "",
      });
      return;
    }
    setAltDraft((prev) => ({ ...prev, [lang]: currentAlt || "" }));
  }, [currentAlt, currentAltI18n, lang]);

  return (
    <div
      className="image-edit-backdrop"
      role="dialog"
      aria-modal="true"
      data-edit-allow="true"
      onClick={onClose}
    >
      <div
        className="image-edit-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid={`image-edit-modal-${slot}`}
      >
        <header className="image-edit-header">
          <div>
            <span className="image-edit-eyebrow">Edit image</span>
            <span className="image-edit-slot">{slot}</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="image-edit-close" data-testid="image-edit-close">
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </header>

        {/* Preview */}
        <div className="image-edit-preview">
          {currentUrl ? (
            <img src={currentUrl} alt={currentAlt || ""} loading="lazy" decoding="async"/>
          ) : (
            <div className="image-edit-preview-empty">No image yet</div>
          )}
        </div>

        {/* Upload */}
        <section className="image-edit-section">
          <label className="image-edit-label">Replace from your device</label>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="image-edit-primary"
            disabled={busy === "upload"}
            data-testid={`image-edit-upload-${slot}`}
          >
            {busy === "upload" ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
            ) : (
              <><Upload className="w-4 h-4" /> Choose file</>
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/avif,image/gif"
            onChange={handleFile}
            className="hidden"
          />
          <p className="image-edit-hint">JPG · PNG · WEBP · AVIF · GIF · up to 8 MB</p>
        </section>

        {/* Bunny Storage */}
        <section className="image-edit-section">
          <label className="image-edit-label">Or pick from Bunny Store</label>
          <button
            type="button"
            onClick={() => setBunnyOpen(true)}
            className="image-edit-secondary"
            disabled={busy === "bunny"}
            data-testid={`image-edit-bunny-${slot}`}
          >
            {busy === "bunny" ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Applying…</>
            ) : (
              <><Database className="w-3.5 h-3.5" /> Select from Bunny Store</>
            )}
          </button>
          <p className="image-edit-hint">Browse your Bunny.net Storage Zone folders and pick an image directly.</p>
        </section>

        {/* Media Library */}
        <section className="image-edit-section">
          <label className="image-edit-label">Or pick from the Media Library</label>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="image-edit-secondary"
            disabled={busy === "library"}
            data-testid={`image-edit-library-${slot}`}
          >
            {busy === "library" ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Applying…</>
            ) : (
              <><FolderOpen className="w-3.5 h-3.5" /> Select from Library</>
            )}
          </button>
          <p className="image-edit-hint">Browse folders or search the central image repository.</p>
        </section>

        {/* Pexels search */}
        <section className="image-edit-section" data-testid="image-edit-pexels-section">
          <label className="image-edit-label" htmlFor={`pexels-${slot}`}>
            Or search free stock photos on Pexels
          </label>
          <div className="image-edit-row">
            <input
              id={`pexels-${slot}`}
              type="search"
              placeholder="e.g. sahara dunes, kasbah, tagine…"
              value={pexelsQuery}
              onChange={(e) => setPexelsQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runPexelsSearch(); } }}
              className="image-edit-input"
              data-testid={`image-edit-pexels-input-${slot}`}
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => runPexelsSearch()}
              className="image-edit-secondary"
              disabled={!pexelsQuery.trim() || pexelsStatus === "loading"}
              data-testid={`image-edit-pexels-search-${slot}`}
            >
              {pexelsStatus === "loading" ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</>
              ) : (
                <><Search className="w-3.5 h-3.5" /> Search</>
              )}
            </button>
          </div>

          {pexelsStatus === "idle" && (
            <p className="image-edit-hint">Royalty-free photos by Pexels. Photographer credit is saved automatically.</p>
          )}
          {pexelsStatus === "loading" && (
            <p className="image-edit-hint" data-testid="image-edit-pexels-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin inline-block align-[-2px] mr-1" />
              Searching Pexels for “{pexelsQuery.trim()}”…
            </p>
          )}
          {pexelsStatus === "empty" && (
            <p className="image-edit-hint" data-testid="image-edit-pexels-empty">
              No photos found for “{pexelsQuery.trim()}”. Try a different keyword.
            </p>
          )}
          {pexelsStatus === "error" && (
            <p className="image-edit-hint" data-testid="image-edit-pexels-error" style={{ color: "#C16542" }}>
              Pexels search failed{pexelsError ? ` — ${pexelsError}` : ""}. Please try again.
            </p>
          )}
          {pexelsStatus === "results" && pexelsPhotos.length > 0 && (
            <div
              className="image-edit-pexels-grid"
              data-testid={`image-edit-pexels-results-${slot}`}
            >
              {pexelsPhotos.map((photo) => {
                const thumb = photo.src?.tiny || photo.src?.small || photo.src?.medium;
                const isApplying = pexelsApplyingId === photo.id;
                return (
                  <div
                    key={`pexels-${photo.id}`}
                    className="image-edit-pexels-card"
                    data-testid={`image-edit-pexels-photo-${photo.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => handlePickPexels(photo)}
                      className="image-edit-pexels-thumb"
                      disabled={busy === "pexels"}
                      aria-label={photo.alt || `Photo by ${photo.photographer}`}
                      title={photo.alt || "Use this photo"}
                    >
                      <img
                        src={thumb}
                        alt={photo.alt || ""}
                        loading="lazy"
                        decoding="async"
                        style={{ backgroundColor: photo.avg_color || "#e8dfcf" }}
                      />
                      {isApplying && (
                        <span className="image-edit-pexels-applying">
                          <Loader2 className="w-4 h-4 animate-spin" />
                        </span>
                      )}
                    </button>
                    <div className="image-edit-pexels-meta">
                      <a
                        href={photo.photographer_url || photo.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="image-edit-pexels-credit"
                        title={`Photo by ${photo.photographer} on Pexels`}
                      >
                        {photo.photographer || "Pexels"}
                        <ExternalLink className="w-3 h-3" strokeWidth={1.8} />
                      </a>
                    </div>
                  </div>
                );
              })}
              <div
                ref={pexelsSentinelRef}
                data-testid={`image-edit-pexels-sentinel-${slot}`}
                className="image-edit-pexels-sentinel"
                aria-hidden={!pexelsHasMore}
              >
                {pexelsLoadingMore && (
                  <span className="image-edit-pexels-sentinel-loader">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Loading more…
                  </span>
                )}
                {!pexelsHasMore && !pexelsLoadingMore && (
                  <span className="image-edit-pexels-sentinel-end">
                    End of results
                  </span>
                )}
              </div>
            </div>
          )}
        </section>

        {/* External URL */}
        <section className="image-edit-section">
          <label className="image-edit-label" htmlFor={`url-${slot}`}>Or paste a URL</label>
          <div className="image-edit-row">
            <input
              id={`url-${slot}`}
              type="url"
              placeholder="https://images.unsplash.com/…"
              value={pasteUrl}
              onChange={(e) => setPasteUrl(e.target.value)}
              className="image-edit-input"
              data-testid={`image-edit-url-${slot}`}
            />
            <button
              type="button"
              onClick={handlePasteUrl}
              className="image-edit-secondary"
              disabled={!pasteUrl.trim() || busy === "url"}
              data-testid={`image-edit-url-save-${slot}`}
            >
              <LinkIcon className="w-3.5 h-3.5" />
              Save URL
            </button>
          </div>
        </section>

        {/* Alt text */}
        <section className="image-edit-section">
          <label className="image-edit-label">Alt text (accessibility)</label>
          {["en", "fr", "es"].map((code) => (
            <div className="image-edit-row" key={`alt-${code}`}>
              <span className="image-edit-lang">{code.toUpperCase()}</span>
              <input
                type="text"
                value={altDraft[code]}
                onChange={(e) => setAltDraft((d) => ({ ...d, [code]: e.target.value }))}
                className="image-edit-input"
                data-testid={`image-edit-alt-${slot}-${code}`}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={handleSaveAlt}
            className="image-edit-secondary"
            disabled={busy === "alt"}
            data-testid={`image-edit-alt-save-${slot}`}
          >
            Save alt text
          </button>
        </section>

        {/* Reset */}
        <section className="image-edit-section image-edit-danger">
          <button
            type="button"
            onClick={handleClear}
            className="image-edit-ghost-danger"
            disabled={busy === "clear"}
            data-testid={`image-edit-clear-${slot}`}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to default
          </button>
        </section>
      </div>
      {pickerOpen && <MediaPicker onClose={() => setPickerOpen(false)} onSelect={handlePickFromLibrary} />}
      {bunnyOpen && <BunnyStoragePicker onClose={() => setBunnyOpen(false)} onSelect={handlePickFromBunny} />}
    </div>
  );
};

export default EditableImage;
